import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Modal,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

import { useTheme } from "@mui/material/styles";
import { useCourse } from "../../hooks";
import Button from "../../../../components/Button";
import TextField from "../../../../components/TextField";
import { ASSESSMENT_TYPES, LEVELS, TOPICS } from "../constants";
import {
  BookBookmark,
  Eye,
  EyeClosed,
  GraduationCap,
  Info,
  Pen,
  X,
} from "@phosphor-icons/react";
import { useGrade } from "../../../gradingModel/hooks";
import {
  createAssessment,
  getAllWithoutPage,
  publishQuestionToAssessment,
  uploadAssessmentById,
} from "../../../../store/course/courseBaseApi";
import { toast as toastFn } from "react-toastify";
import Loader from "../../../../components/Loader";
import { ManageQuestions } from "./ManageQuestions";
import { useQuestionBank } from "../../../questionBank/hooks";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import GenerateQuestion from "../GenerateQuestion";
import { CreateQuestion } from "./CreateQuestion";
import { MarkupEditor } from "../../../../components/markupEditor";
import { styled } from "@mui/material/styles";
import GradingModelView from "../../lib/GradingModelView";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top" arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
  },
  [`& .${tooltipClasses.arrow}`]: {},
}));

export const ManageAssessment = ({
  assessmentId = null,
  chapterId,
  sectionId,
  edit,
  setEdit,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const {
    loadLessonPlan,
    lessonPlan,
    updateAssessment,
    publishAssessmentChapterToLessonPlan,
    publishAssessmentSectionToLessonPlan,
    publishAssessmentToCourse,
    loadCurrentAssessment,
    currentAssessment,
    loading: courseLoading,
  } = useCourse();
  const { loadGrades, gradeList } = useGrade();
  const {
    loadCourseQuestions,
    courseQuestions,
    loading: questionsLoading,
  } = useQuestionBank();
  const [assessment, setAssessment] = useState({});
  const [sectionData, setSectionData] = useState();
  const [chapterData, setChapterData] = useState();
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [courseList, setCourseList] = useState();
  const [sectionList, setSectionList] = useState();
  const [chapterList, setChapterList] = useState();
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [isDiagnostic, setIsDiagnostic] = useState(false);
  const [questionId, setQuestionId] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");

  const handleClose = () => {
    setOpenModal(!openModal);
    if (questionId) {
      setQuestionId("");
    }
  };

  const showToast = (msg, type) => {
    if (!type) {
      return toastFn(msg);
    }
    toastFn[type](msg);
  };

  const handleModal = (type) => {
    if (type === "generate") {
      setOpenModal(true);
      setModalType(type);
    } else if (type === "create") {
      setOpenModal(true);
      setModalType(type);
    } else if (type === "manage") {
      setOpenModal(true);
      setModalType(type);
    }
  };

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setAssessment({ ...assessment, [name]: value });
    if (name === "gradingModel") {
      const selectedGradingModel = gradeList.data.find(
        (model) => model.id === value,
      );
      setFilteredGrades(selectedGradingModel?.details || []);
    }
  };

  const handleCheckboxChange = (question) => {
    const currentQuestionIndex = selectedQuestions.findIndex(
      (q) => q.id === question.id,
    );

    if (currentQuestionIndex > -1) {
      setSelectedQuestions(
        selectedQuestions.filter((q) => q.id !== question.id),
      );
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const getMaxGradeMark = () => {
    if (filteredGrades.length > 0) {
      return Math.max(
        ...filteredGrades.map((grade) => parseInt(grade.max, 10)),
      );
    }
    return 0;
  };

  let displayMaxMarks = getMaxGradeMark();

  useEffect(() => {
    // loadLessonPlan({ id: courseId });
    if (chapterId && sectionId) {
      loadCourseQuestions({
        courseId: courseId,
        sectionId: sectionId,
        chapterId: chapterId,
      });
    } else if (courseId && sectionId) {
      loadCourseQuestions({ courseId: courseId, sectionId: sectionId });
    } else {
      loadCourseQuestions({ courseId: courseId });
    }
    loadGrades();
  }, [courseId, sectionId, chapterId]);

  useEffect(() => {
    if (lessonPlan) {
      const section = lessonPlan?.sections?.find(
        (sec) => sec.sectionId === sectionId,
      );
      setSectionData(section);
      if (section) {
        const chapter = section?.chapters?.find(
          (chap) => chap.chapterId === chapterId,
        );
        setChapterData(chapter);
      }
    }
  }, [lessonPlan]);

  const fetchCourses = async () => {
    try {
      let { data } = await getAllWithoutPage({
        payload: { pageSize: 500 },
      });
      setCourseList(data?.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (assessmentId) {
      loadCurrentAssessment({ id: assessmentId });
    }
  }, [assessmentId]);

  useEffect(() => {
    if (courseList) {
      const diagnostic = !!courseList?.find(
        (course) =>
          course?.id === courseId && course?.category === "Diagnostic",
      );
      setIsDiagnostic(diagnostic);
    }
  }, [courseList]);

  useEffect(() => {
    if (currentAssessment) {
      const questionIds = currentAssessment?.questions?.map(
        (q) => q.questionId,
      );
      const selectedQues = courseQuestions?.data?.filter((question) =>
        questionIds?.includes(question.id),
      );
      setSelectedQuestions(selectedQues || []);
      setAssessment({
        ...currentAssessment,
        gradingModel:
          gradeList?.data?.find(
            (model) =>
              model.gradingModelName ===
              currentAssessment?.gradingModel?.gradingModelName,
          )?.id || "",
        timed: currentAssessment?.isTimed === "1" ? 1 : 0,
      });
      const selectedGradingModel = gradeList?.data?.find(
        (model) =>
          model.gradingModelName ===
          currentAssessment?.gradingModel?.gradingModelName,
      );
      setFilteredGrades(selectedGradingModel?.details || []);
    }
  }, [currentAssessment, gradeList]);

  useEffect(() => {
    if (courseId) {
      setSectionList(lessonPlan?.sections);
      const selectedSection = lessonPlan?.sections?.find(
        (sec) => sec.sectionId === sectionId || assessment?.sectionId,
      );
      setChapterList(selectedSection?.chapters);
    }
  }, [lessonPlan, sectionId, assessment]);

  useEffect(() => {
    const selectedQuestionMarks = selectedQuestions?.reduce(
      (sum, question) => sum + (question?.answerDetails?.options?.marks || 0),
      0,
    );
    setAssessment({
      ...assessment,
      noOfQuestions: selectedQuestions?.length,
      totalMarks: selectedQuestionMarks,
    });
  }, [courseQuestions, selectedQuestions]);

  useEffect(() => {
    if (assessmentId && lessonPlan && Object.keys(lessonPlan).length) {
      let section = lessonPlan.sections.find((e) => e.sectionId === sectionId);
      // let chapter = section.chapters.find(e => e.chapterId === chapterId);

      // if (chapter.assessments) {
      // let _assessment = chapter.assessments.find(e => e.id === id);
      // }
    }
  }, [lessonPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const maxGradeMark = getMaxGradeMark();
    let navigatePath = isDiagnostic
      ? `/diagnostic/${courseId}`
      : `/courses/${courseId}/lesson-plan`;
    if (assessment.totalMarks !== maxGradeMark) {
      showToast(
        `Total marks (${assessment.totalMarks}) must equal the highest mark of the selected grading model (${maxGradeMark}).`,
        "error",
      );
      return;
    }

    let quesForSubmit = selectedQuestions.map((question, index) => {
      return {
        groupId: question?.groupId || "",
        questionId: question?.id,
        order: index + 1,
      };
    });
    let temp = {
      courseId: courseId,
      sectionId: sectionId || assessment?.sectionId,
      chapterId: chapterId || assessment?.chapterId,
      name: assessment?.name,
      // topic: assessment?.topic,
      level: assessment?.level,
      type: assessment?.type,
      timeAllowed: assessment?.timeAllowed,
      noOfQuestions: assessment?.noOfQuestions,
      totalMarks: assessment?.totalMarks,
      gradingModel: assessment?.gradingModel,
      passGrade: assessment?.passGrade,
      isTimed: assessment?.timed,
      questions: quesForSubmit,
    };
    if (assessmentId) {
      temp = { ...temp, id: assessmentId, courseCode: lessonPlan?.courseCode };
      updateAssessment({ assessment: temp, navigate, path: navigatePath });
    } else {
      const response = await createAssessment(temp);
      let publishData = {
        courseId: response?.data?.courseId,
        sectionId: response?.data?.sectionId,
        chapterId: response?.data?.chapterId,
        assessmentId: response?.data?.id,
        id: lessonPlan?.id,
      };
      let publishCourseData = {
        courseId: response?.data?.courseId,
        assessmentId: response?.data?.id,
        id: lessonPlan?.id,
      };

      const assessmentResponse = await publishQuestionToAssessment({
        id: response?.data?.id,
      });
      const json = JSON.stringify(assessmentResponse, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const formData = new FormData();
      formData.append("file", blob, `${response?.data?.id}.json`);
      try {
        const resp = await uploadAssessmentById({ id: assessmentId, formData });

        if (resp.message) {
          // console.log("resp", resp);
        }
      } catch (e) {
        return showToast(e.error, "error");
      }
      if (chapterId) {
        publishAssessmentChapterToLessonPlan({ assessment: publishData });
        loadLessonPlan({ id: courseId });
        setEdit(!edit);
      } else if (!chapterId && sectionId) {
        publishAssessmentSectionToLessonPlan({ assessment: publishData });
        loadLessonPlan({ id: courseId });
        setEdit(!edit);
      } else if (!sectionId && courseId) {
        publishAssessmentToCourse({ assessment: publishCourseData });
        loadLessonPlan({ id: courseId });
        setEdit(!edit);
      }
    }
  };

  // const cancelChanges = () => isDiagnostic ? navigate(`/diagnostic/${courseId}/assessment`) : navigate(`/courses/${courseId}/lesson-plan`)
  const cancelChanges = () => setEdit(!edit);

  return (
    <>
      {courseLoading ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              padding: "0px 20px",
              [theme.breakpoints.down("md")]: {
                padding: "0px",
              },
            }}
          >
            <div className="course header-container">
              <div className="header">
                <Typography
                  align="center"
                  variant="h6"
                  color="primary.blue"
                  sx={{
                    margin: "0",
                    fontWeight: "700",
                    lineHeight: "1.5",
                  }}
                >
                  {assessmentId ? `Edit` : "Create"} Assessment
                </Typography>
                {/* <Typography
                                    align="left"
                                    variant="h1"
                                    color="primary.blue"
                                    sx={{
                                        margin: '0',
                                        fontWeight: '700',
                                        lineHeight: '1.5',
                                        fontSize: '2rem',
                                    }}>{assessmentId ? `Edit` : 'Create'} Assessment</Typography> */}
              </div>
            </div>
            {/* <Grid sx={styles.gridContainer} container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <InputLabel style={styles.inputLabel}>Course</InputLabel>
                                    <FormControl fullWidth>
                                        <Select
                                            name="courseId"
                                            value={courseId || ''}
                                            disabled
                                            placeholder="Course"
                                            sx={{ height: '47px' }}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {courseList?.map((item, index) => {
                                                return <MenuItem key={index} value={item?.id}>{item?.courseName}</MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <InputLabel style={styles.inputLabel}>Section</InputLabel>
                                    <FormControl fullWidth>
                                        <Select
                                            name="sectionId"
                                            value={assessment?.sectionId || sectionId || ''}
                                            disabled={sectionId}
                                            onChange={(e) => handleChange(e)}
                                            placeholder="Section"
                                            sx={{ height: '47px' }}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {sectionList?.map((item, index) => {
                                                return <MenuItem key={index} value={item?.sectionId}>{item?.name}</MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <InputLabel style={styles.inputLabel}>Chapter</InputLabel>
                                    <FormControl fullWidth>
                                        <Select
                                            name="chapterId"
                                            value={assessment?.chapterId || chapterId || ''}
                                            disabled={chapterId}
                                            placeholder="Chapter"
                                            onChange={(e) => handleChange(e)}
                                            sx={{ height: '47px' }}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {chapterList?.map((item, index) => {
                                                return <MenuItem key={index} value={item?.chapterId}>{item?.name}</MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid> */}
            <Grid
              sx={styles.gridContainer}
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>Name</InputLabel>
                <TextField
                  name="name"
                  fullWidth
                  value={assessment?.name || ""}
                  onChange={(e) => handleChange(e)}
                  placeholder="Assessment Name"
                />
              </Grid>
              {/* <Grid item xs={12} sm={6} md={4} lg={3}> */}
              {/* <InputLabel style={styles.inputLabel}>Topic</InputLabel>
                            <FormControl fullWidth>
                                <Select
                                    name="topic"
                                    placeholder="Topic"
                                    value={assessment?.topic || ''}
                                    sx={{ height: '47px' }}
                                    onChange={(e) => handleChange(e)}
                                    disabled={!assessment?.name}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {Object.keys(TOPICS).map((key, i) => (
                                        <MenuItem key={i} value={key}>{TOPICS[key]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl> */}
              {/* <InputLabel style={styles.inputLabel}>Topic</InputLabel>
                            <TextField
                                name="topic"
                                fullWidth
                                value={assessment?.topic || ''}
                                onChange={(e) => handleChange(e)}
                                placeholder="Topic"
                            />
                        </Grid> */}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>Level</InputLabel>
                <FormControl fullWidth>
                  <Select
                    name="level"
                    placeholder="Level"
                    value={assessment?.level || ""}
                    sx={{ height: "47px" }}
                    onChange={(e) => handleChange(e)}
                    disabled={!assessment?.name}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.keys(LEVELS).map((key, i) => (
                      <MenuItem key={i} value={key}>
                        {LEVELS[key]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>Type</InputLabel>
                <FormControl fullWidth>
                  <Select
                    name="type"
                    placeholder="Type"
                    value={assessment?.type || ""}
                    sx={{ height: "47px" }}
                    onChange={handleChange}
                    disabled={!assessment?.level}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.keys(ASSESSMENT_TYPES).map((key, i) => (
                      <MenuItem key={i} value={key}>
                        {ASSESSMENT_TYPES[key]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  Time Alloted (Min)
                </InputLabel>
                <TextField
                  name="timeAllowed"
                  fullWidth
                  type="number"
                  value={assessment?.timeAllowed}
                  onChange={({ target }) => {
                    if (target.value >= 0) {
                      setAssessment((prev) => ({
                        ...prev,
                        [target.name]: target.value,
                      }));
                    }
                  }}
                  placeholder="Time Alloted"
                  disabled={!assessment?.type}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  Grading Model
                  {displayMaxMarks ? `(Total Marks ${displayMaxMarks})` : ""}
                </InputLabel>
                <FormControl fullWidth>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // border: "1px solid #ccc",
                      borderRadius: "4px",
                      overflow: "hidden",
                      position: "relative",
                      height: "47px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <LightTooltip
                      arrow
                      title={
                        <Card
                          sx={{ width: "80vh", background: "#FFCB9B", p: 1 }}
                        >
                          <CardContent
                            sx={{ background: "#fff", borderRadius: 1 }}
                          >
                            <GradingModelView
                              totalMarks={displayMaxMarks}
                              data={assessment}
                              gradeId={assessment?.gradingModel}
                              grades={gradeList}
                            />
                          </CardContent>
                        </Card>
                      }
                    >
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          zIndex: 1, // Positioning the icon before the Select arrow
                          left: 8, // Adjust based on the padding or space you want
                          backgroundColor: "#f5f5f5",
                          "&:hover": { backgroundColor: "#e0e0e0" },
                        }}
                      >
                        <GraduationCap />
                      </IconButton>
                    </LightTooltip>
                    <Select
                      name="gradingModel"
                      value={assessment?.gradingModel || ""}
                      onChange={(e) => handleChange(e)}
                      placeholder="Grading Model"
                      fullWidth
                      sx={{ height: "100%", flex: 1, paddingLeft: "32px" }}
                      disabled={!assessment?.timeAllowed}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {gradeList?.data?.map((item, index) => (
                        <MenuItem key={index} value={item?.id}>
                          {item?.gradingModelName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>Passing Grade</InputLabel>
                <FormControl fullWidth>
                  <Select
                    name="passGrade"
                    value={assessment?.passGrade || ""}
                    onChange={(e) => handleChange(e)}
                    placeholder="Pass Grade"
                    sx={{ height: "47px" }}
                    disabled={!assessment?.gradingModel}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {filteredGrades?.map((item, index) => {
                      return (
                        <MenuItem key={index} value={item.gradeName}>
                          {item.gradeName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>Timed</InputLabel>
                <FormControl fullWidth>
                  <Select
                    name="timed"
                    placeholder="Timed"
                    sx={{ height: "47px" }}
                    value={assessment?.timed || ""}
                    onChange={handleChange}
                    disabled={!assessment?.passGrade}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  No. of questions
                </InputLabel>
                <TextField
                  name="noOfQuestions"
                  fullWidth
                  type="number"
                  value={assessment?.noOfQuestions || " "}
                  onChange={handleChange}
                  placeholder="No. of questions"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>Total marks</InputLabel>
                <TextField
                  name="totalMarks"
                  fullWidth
                  type="number"
                  value={assessment?.totalMarks || ""}
                  onChange={handleChange}
                  placeholder="Total marks"
                  disabled
                />
              </Grid>
            </Grid>
            {/* <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <MarkupEditor
                                        isAssessment={true}
                                        disabled={true}
                                        content={chapterData?.content}
                                        keyWords={chapterData?.keyWords}
                                        irLevels={Array.from(new Array(9)).map((e, i) => `L${i}`)}
                                    />
                                </Grid>
                            </Grid> */}
            <Grid
              sx={{ mt: 1, display: "flex", justifyContent: "center" }}
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={4}
                lg={3}
                display={"flex"}
                justifyContent={"center"}
              >
                <Button
                  onClick={() => handleModal("manage")}
                  variant="contained"
                  color="secondary"
                  placeholder={"Manage Questions"}
                  endIcon={<BookBookmark />}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                lg={3}
                display={"flex"}
                justifyContent={"center"}
              >
                <Button
                  onClick={() => handleModal("create")}
                  variant="contained"
                  color="primary"
                  placeholder={"Create Question"}
                  endIcon={<Pen />}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                lg={3}
                display={"flex"}
                justifyContent={"center"}
              >
                <Button
                  variant="contained"
                  style={styles.btnAnimation}
                  onClick={() => handleModal("generate")}
                  placeholder={"Generate Question"}
                  endIcon={<AutoFixHighIcon />}
                />
              </Grid>
              {openModal && modalType === "manage" && (
                <Modal open={openModal}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "80%",
                      height: "80%",
                      bgcolor: "background.paper",
                      boxShadow: 24,
                      borderRadius: "10px",
                      p: 4,
                      overflow: "auto",
                    }}
                  >
                    <ManageQuestions
                      handleClose={handleClose}
                      handleCheckboxChange={handleCheckboxChange}
                      selectedQuestions={selectedQuestions}
                      courseQuestions={courseQuestions}
                      setModalType={setModalType}
                      setQuestionId={setQuestionId}
                    />
                  </Box>
                </Modal>
              )}
              {openModal && modalType === "create" && (
                <Modal open={openModal}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "80%",
                      height: "80%",
                      bgcolor: "background.paper",
                      boxShadow: 24,
                      borderRadius: "10px",
                      p: 4,
                      overflow: "auto",
                    }}
                  >
                    <CreateQuestion
                      handleClose={handleClose}
                      questionId={questionId}
                      setQuestionId={setQuestionId}
                      setModalType={setModalType}
                      sectionId={sectionId}
                      chapterId={chapterId}
                      lessonPlanId={lessonPlan?.id}
                    />
                  </Box>
                </Modal>
              )}
              {openModal && modalType === "generate" && (
                <Modal open={openModal}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "80%",
                      height: "80%",
                      bgcolor: "background.paper",
                      boxShadow: 24,
                      borderRadius: "10px",
                      p: 4,
                      overflow: "auto",
                    }}
                  >
                    <GenerateQuestion
                      lessonPlanData={lessonPlan}
                      sectionData={sectionData}
                      chapterData={chapterData}
                      handleClose={handleClose}
                    />
                  </Box>
                </Modal>
              )}
            </Grid>
            <Box
              sx={{ mt: 4, p: 2, display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                onClick={() => cancelChanges()}
                variant="outlined"
                placeholder={"Cancel"}
                sx={{ mr: 2 }}
              />
              <Button
                onClick={handleSubmit}
                disabled={!assessment?.timed || !assessment?.totalMarks}
                variant="contained"
                color="primary"
                placeholder={
                  !assessmentId ? "Create Assessment" : "Save Assessment"
                }
              />
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "14px",
  },
  gridContainer: {
    mt: 2,
  },
  btnAnimation: {
    background: "linear-gradient(270deg, violet, #F7006F)",
    backgroundSize: "400% 400%",
    animation: "gradientAnimation 3s ease infinite",
    color: "#fff", // Make sure text is readable over the gradient
    // borderRadius: '20px',
    // padding: '10px 20px',
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    border: "none",
  },
  textGradient: {
    background: "linear-gradient(270deg, red, var(--mui-palette-primary-main))",
    backgroundSize: "200% 200%", // Makes the gradient "flow"
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent", // Ensures only the gradient is visible
    animation: "textGradientAnimation 3s ease infinite",
  },
  tooltipCard: {
    width: "300px",
    padding: "16px",
  },
};

// Inject keyframes into the document (useEffect ensures it runs once)
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(
  `
  @keyframes gradientAnimation {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`,
  styleSheet.cssRules.length,
);

styleSheet.insertRule(
  `
  @keyframes textGradientAnimation {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`,
  styleSheet.cssRules.length,
);
